// Quote list. Same shape as Invoices but quote-specific statuses.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { formatDate, formatMoney } from '../lib/format';

interface QuoteRow {
  id: string; number: string; issue_date: string; expiry_date: string;
  status: string; total: number; currency: string; contact_name: string;
  converted_to_invoice_id: string | null;
}

export default function Quotes() {
  const { currentOrg } = useOrg();
  const [rows, setRows] = useState<QuoteRow[]>([]);
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'sent' | 'accepted' | 'declined' | 'expired' | 'converted'>('all');

  useEffect(() => {
    if (!currentOrg) return;
    (async () => {
      const { data } = await supabase
        .from('dodobooks_quotes')
        .select('id, number, issue_date, expiry_date, status, total, currency, converted_to_invoice_id, dodobooks_contacts(name)')
        .eq('org_id', currentOrg.id)
        .order('issue_date', { ascending: false });
      setRows(((data ?? []) as any[]).map((r) => ({
        id: r.id, number: r.number, issue_date: r.issue_date, expiry_date: r.expiry_date,
        status: r.status, total: r.total, currency: r.currency,
        contact_name: r.dodobooks_contacts?.name ?? '—',
        converted_to_invoice_id: r.converted_to_invoice_id,
      })));
    })();
  }, [currentOrg]);

  const filtered = rows.filter((r) => statusFilter === 'all' || r.status === statusFilter);
  const totals = filtered.reduce((acc, r) => { acc.total += Number(r.total); return acc; }, { total: 0 });
  const accepted = rows.filter((r) => r.status === 'accepted').reduce((s, r) => s + Number(r.total), 0);

  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="h-page">Quotes</h1>
          <p className="text-text-dim text-sm mt-1">Estimates and proposals. Convert accepted ones to invoices in one click.</p>
        </div>
        <Link to="/quotes/new" className="btn btn-primary"><Plus size={14} /> New quote</Link>
      </header>

      <div className="flex gap-3 items-center flex-wrap">
        <div className="flex bg-surface border border-line rounded-md overflow-hidden">
          {(['all', 'draft', 'sent', 'accepted', 'declined', 'expired', 'converted'] as const).map((f) => (
            <button key={f} type="button" onClick={() => setStatusFilter(f)}
              className={'px-3 py-1.5 text-xs font-mono uppercase tracking-wider ' + (statusFilter === f ? 'bg-emerald/10 text-emerald' : 'text-text-mute hover:text-text')}>{f}</button>
          ))}
        </div>
        <div className="ml-auto text-sm text-text-dim">
          <span className="num text-emerald">{formatMoney(accepted)}</span> accepted · <span className="num text-text-dim">{formatMoney(totals.total)}</span> shown
        </div>
      </div>

      <div className="card p-0">
        {filtered.length === 0 ? (
          <div className="text-text-mute text-sm py-12 text-center">
            No quotes here. <Link to="/quotes/new" className="text-emerald hover:underline">Create one.</Link>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th className="px-5">Number</th><th>Customer</th><th>Issued</th><th>Expires</th>
                <th>Status</th><th className="text-right pr-5">Total</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id}>
                  <td className="px-5"><Link to={'/quotes/' + r.id} className="text-emerald hover:underline font-medium">{r.number}</Link></td>
                  <td>{r.contact_name}</td>
                  <td className="text-text-dim">{formatDate(r.issue_date)}</td>
                  <td className="text-text-dim">{formatDate(r.expiry_date)}</td>
                  <td>
                    <span className={'pill ' + (r.status === 'accepted' ? 'pill-paid' : r.status === 'sent' ? 'pill-sent' : r.status === 'expired' || r.status === 'declined' ? 'pill-overdue' : r.status === 'converted' ? 'pill-paid' : 'pill-draft')}>
                      {r.status}
                    </span>
                  </td>
                  <td className="text-right num pr-5">{formatMoney(r.total, r.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
